import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  AD: "",
  TIP: "Satici",
  HAK_ADEDI: "",
  FIYAT: "",
  AKTIF: true,
  SKU_IOS: "",
  SKU_ANDROID: "",
  KATEGORI_ID: "",
};

// Backend (IapController.Verify) bu PascalCase degerlerle eslesir.
// Satici/Uretici/Magaza -> ILAN_HAKKI
// Mezat (AD'de "Uzatma" yoksa) -> MEZAT_HAKKI; (AD'de "Uzatma" varsa) -> MEZAT_UZATMA_HAKKI
// Degerleme -> DEGERLEME_HAKKI
const TIP_OPTIONS = [
  { value: "Satici", label: "İlan (Satıcı)" },
  { value: "Uretici", label: "İlan (Üretici)" },
  { value: "Magaza", label: "İlan (Mağaza)" },
  { value: "Mezat", label: "Mezat / Mezat Uzatma" },
  { value: "Degerleme", label: "Değerleme" },
];

const TIP_RENK = {
  satici: "info",
  uretici: "info",
  magaza: "info",
  mezat: "warning",
  degerleme: "success",
};

const Paketler = () => {
  document.title = "Paketler | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [kategoriler, setKategoriler] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/PAKETLER?$orderby=ID desc&$expand=KATEGORILER`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("Paketler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchKategoriler = async () => {
    try {
      const [resKat, resBag] = await Promise.all([
        fetch(`${BASE_URL}/KATEGORILER?$orderby=SIRA_NO asc`, { headers }),
        fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI?$select=ALT_KATEGORI_ID&$top=10000`, { headers }),
      ]);
      const katJson = await resKat.json();
      const bagJson = await resBag.json();
      const altIds = new Set((bagJson.value || []).map(b => b.ALT_KATEGORI_ID));
      // Sadece ana kategoriler — alt olarak gozukmeyenler
      const anaKategoriler = (katJson.value || []).filter(k => !altIds.has(k.ID));
      setKategoriler(anaKategoriler);
    } catch {
      // sessizce gec — modal yine acilabilir
    }
  };

  useEffect(() => { fetchListe(); fetchKategoriler(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      ID: row.ID,
      AD: row.AD || "",
      TIP: row.TIP || "Satici",
      HAK_ADEDI: row.HAK_ADEDI ?? "",
      FIYAT: row.FIYAT ?? "",
      AKTIF: row.AKTIF !== false,
      SKU_IOS: row.SKU_IOS || "",
      SKU_ANDROID: row.SKU_ANDROID || "",
      KATEGORI_ID: row.KATEGORI_ID ?? "",
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.AD.trim()) {
      setError("Paket adı zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        AD: formData.AD,
        TIP: formData.TIP,
        HAK_ADEDI: formData.HAK_ADEDI !== "" ? Number(formData.HAK_ADEDI) : null,
        FIYAT: formData.FIYAT !== "" ? parseFloat(formData.FIYAT) : null,
        AKTIF: formData.AKTIF,
        SKU_IOS: formData.SKU_IOS || null,
        SKU_ANDROID: formData.SKU_ANDROID || null,
        KATEGORI_ID: formData.KATEGORI_ID !== "" ? Number(formData.KATEGORI_ID) : null,
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/PAKETLER(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Paket güncellendi.");
      } else {
        await fetch(`${BASE_URL}/PAKETLER`, {
          method: "POST", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Paket eklendi.");
      }

      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu paketi silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/PAKETLER(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Paket silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Paket Adı",
      accessorKey: "AD",
      enableSorting: true,
      cell: (c) => <span className="fw-medium">{c.getValue() || "---"}</span>,
    },
    {
      header: "Tip",
      accessorKey: "TIP",
      enableSorting: true,
      cell: (c) => {
        const tip = c.getValue();
        const color = TIP_RENK[tip?.toLowerCase()] || "secondary";
        return <Badge color={color} pill className="text-capitalize">{tip || "---"}</Badge>;
      },
    },
    {
      header: "Kategori",
      accessorKey: "KATEGORI_ID",
      enableSorting: true,
      cell: (c) => {
        const row = c.row.original;
        const ad = row.KATEGORILER?.AD;
        if (ad) return <Badge color="secondary" pill>{ad}</Badge>;
        if (c.getValue()) return <Badge color="light" className="text-dark">#{c.getValue()}</Badge>;
        return <span className="text-muted">Tüm</span>;
      },
    },
    {
      header: "Hak Adedi",
      accessorKey: "HAK_ADEDI",
      enableSorting: true,
      cell: (c) => c.getValue() != null
        ? <Badge color="light" className="text-dark fs-6 px-3">{c.getValue()}</Badge>
        : <span className="text-muted">---</span>,
    },
    {
      header: "Fiyat",
      accessorKey: "FIYAT",
      enableSorting: true,
      cell: (c) => c.getValue() != null
        ? <span className="fw-bold text-success">{parseFloat(c.getValue()).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</span>
        : <span className="text-muted">---</span>,
    },
    {
      header: "iOS SKU",
      accessorKey: "SKU_IOS",
      enableSorting: false,
      cell: (c) => c.getValue()
        ? <code className="small">{c.getValue()}</code>
        : <span className="text-muted">---</span>,
    },
    {
      header: "Android SKU",
      accessorKey: "SKU_ANDROID",
      enableSorting: false,
      cell: (c) => c.getValue()
        ? <code className="small">{c.getValue()}</code>
        : <span className="text-muted">---</span>,
    },
    {
      header: "Durum",
      accessorKey: "AKTIF",
      enableSorting: false,
      cell: (c) => (
        <Badge color={c.getValue() ? "success" : "danger"}>
          {c.getValue() ? "Aktif" : "Pasif"}
        </Badge>
      ),
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(row)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="Paketler" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">Paket Yönetimi</h4>
                      <p className="text-muted small mb-0">
                        Kullanıcıların satın alabileceği ilan, mezat ve değerleme paketlerini buradan yönetin.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Paket
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Paket ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-package-variant" : "mdi-package-variant-plus"} me-2 text-primary`}></i>
          {isEditing ? `Paketi Düzenle (#${formData.ID})` : "Yeni Paket Ekle"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={8}>
              <FormGroup>
                <Label className="fw-bold">Paket Adı <span className="text-danger">*</span></Label>
                <Input
                  value={formData.AD}
                  onChange={(e) => f("AD", e.target.value)}
                  placeholder="Örn: 10 İlan Paketi"
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Paket Tipi</Label>
                <Input type="select" value={formData.TIP} onChange={(e) => f("TIP", e.target.value)}>
                  {TIP_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Input>
                <small className="text-muted">
                  Mezat süre uzatma paketi için TIP=Mezat seçin ve <strong>paket adına "Uzatma" ekleyin</strong>.
                </small>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">
                  Kategori <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={formData.KATEGORI_ID}
                  onChange={(e) => f("KATEGORI_ID", e.target.value)}
                >
                  <option value="">— Ana kategori seçin (boş = tüm kategoriler / eski paket) —</option>
                  {kategoriler.map((k) => (
                    <option key={k.ID} value={k.ID}>{k.AD}</option>
                  ))}
                </Input>
                <small className="text-muted">
                  Yalnızca ana kategoriler listelenir; alt kategorilerin hakları üst kategoriden düşülür.
                </small>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Hak Adedi</Label>
                <Input
                  type="number" min="1"
                  value={formData.HAK_ADEDI}
                  onChange={(e) => f("HAK_ADEDI", e.target.value)}
                  placeholder="10"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Fiyat (₺)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={formData.FIYAT}
                  onChange={(e) => f("FIYAT", e.target.value)}
                  placeholder="49.99"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">iOS SKU</Label>
                <Input
                  value={formData.SKU_IOS}
                  onChange={(e) => f("SKU_IOS", e.target.value)}
                  placeholder="com.harmanagel.ilan10"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Android SKU</Label>
                <Input
                  value={formData.SKU_ANDROID}
                  onChange={(e) => f("SKU_ANDROID", e.target.value)}
                  placeholder="harmanagel_ilan_10"
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup check className="mt-1">
                <Input
                  type="checkbox"
                  id="paketAktif"
                  checked={formData.AKTIF}
                  onChange={(e) => f("AKTIF", e.target.checked)}
                />
                <Label check for="paketAktif" className="fw-bold ms-1">
                  Aktif — uygulamada gösterilsin
                </Label>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Paketler;
